import { __assign, __rest } from 'tslib';
import { jsx, Fragment } from '@emotion/react/jsx-runtime';
import { useMemo, useEffect } from 'react';
import { positionDefault, positionRight } from '../../Popover/usePopover.js';
import { renderProp } from '../../utils/renderProp.js';
import { useMenuDropdownContext } from '../MenuContext/MenuProvider.js';

var getActiveElementIndex = function (menuItemRefs) {
    return menuItemRefs.findIndex(function (ref) { return document.activeElement === (ref === null || ref === void 0 ? void 0 : ref.current); });
};
var getNextElementIndex = function (menuItemRefs) {
    var activeIndex = getActiveElementIndex(menuItemRefs);
    if (activeIndex !== -1) {
        var i = activeIndex + 1;
        while (i !== activeIndex) {
            var ref = menuItemRefs[i];
            if ((ref === null || ref === void 0 ? void 0 : ref.current) &&
                !ref.current.disabled &&
                ref.current.getAttribute("data-disabled") !== "true") {
                return i;
            }
            i = i >= menuItemRefs.length - 1 ? 0 : i + 1;
        }
    }
    return activeIndex;
};
var getPrevElementIndex = function (menuItemRefs) {
    var activeIndex = getActiveElementIndex(menuItemRefs);
    if (activeIndex !== -1) {
        if (activeIndex === -1)
            return -1;
        var i = activeIndex - 1;
        while (i !== activeIndex) {
            var ref = menuItemRefs[i];
            if ((ref === null || ref === void 0 ? void 0 : ref.current) &&
                !ref.current.disabled &&
                ref.current.getAttribute("data-disabled") !== "true")
                return i;
            i = i <= 0 ? menuItemRefs.length - 1 : i - 1;
        }
    }
    return activeIndex;
};
var focusNextElement = function (menuItemRefs) {
    var _a;
    if (!menuItemRefs.length)
        return;
    var ref = menuItemRefs[getNextElementIndex(menuItemRefs)];
    (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.focus();
};
var focusPrevElement = function (menuItemRefs) {
    var _a;
    if (!menuItemRefs.length)
        return;
    var ref = menuItemRefs[getPrevElementIndex(menuItemRefs)];
    (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.focus();
};
var createKeyDownHandler = function (menuItemRefs) {
    if (!menuItemRefs)
        return undefined;
    return function (event) {
        switch (event.code) {
            case "Tab":
                event.preventDefault();
                break;
            case "ArrowDown":
                event.preventDefault();
                focusNextElement(menuItemRefs);
                break;
            case "ArrowUp":
                event.preventDefault();
                focusPrevElement(menuItemRefs);
                break;
        }
    };
};
var createGlobalKeyDownHandler = function (_a) {
    var menuItemRefs = _a.menuItemRefs, activeItemId = _a.activeItemId, isOpen = _a.isOpen, setIsOpen = _a.setIsOpen, triggerRef = _a.triggerRef;
    return function (event) {
        var _a, _b;
        switch (event.code) {
            case "ArrowUp":
            case "ArrowDown":
                if (isOpen && !activeItemId && ((_a = menuItemRefs.current) === null || _a === void 0 ? void 0 : _a.length)) {
                    // prevent scroll
                    event.preventDefault();
                    (_b = triggerRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                }
                break;
            case "Escape":
                isOpen && setIsOpen(false);
                break;
        }
    };
};
var useMenuList = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.align, align = _c === void 0 ? "left" : _c, portal = _b.portal;
    var context = useMenuDropdownContext();
    var menuItemRefs = context.menuItemRefs, id = context.id, activeItemId = context.activeItemId;
    var keyDownHandler = useMemo(function () { return createKeyDownHandler(menuItemRefs.current); }, [menuItemRefs]);
    var globalKeyDownHandler = createGlobalKeyDownHandler(context);
    useEffect(function () {
        document.addEventListener("keydown", globalKeyDownHandler);
        return function () {
            document.removeEventListener("keydown", globalKeyDownHandler);
        };
    }, [globalKeyDownHandler]);
    return {
        props: __assign(__assign(__assign({ id: "".concat(id, "-dropdown"), portal: portal, autoFocus: false, role: "menu", "aria-labelledby": id, onKeyDown: keyDownHandler }, (align === "left" && { position: positionDefault })), (align === "right" && { position: positionRight })), (activeItemId && {
            "aria-activedescendant": activeItemId,
        })),
    };
};
var MenuListRenderProp = function (_a) {
    var children = _a.children, props = __rest(_a, ["children"]);
    var returnVal = useMenuList(props);
    return jsx(Fragment, { children: renderProp({ prop: children, context: returnVal }) });
};

export { MenuListRenderProp, useMenuList };
//# sourceMappingURL=useMenuList.js.map
